import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable sonarjs/no-duplicate-string */
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, useBreakpointValue } from "@gemini/core";
import { CloseIcon } from "@gemini/icons";
import { Button } from "@gemini/ui";
import { useEnvironment } from "@aviv-unified-frontend/runtime/environment";
import { STATIC_ASSETS_PREFIX } from "../../../constants/assets";
import { BRAND_CONFIGURATION } from "../../../../configs/brands";
import { useTranslate } from "../../../utils/useTranslate";
import { useCustomBanner } from "./useCustomBanner";
const { appBanner } = BRAND_CONFIGURATION;
export const CustomBanner = ({ onDismissCallback, }) => {
    const t = useTranslate();
    const { application: { id: applicationId }, } = useEnvironment();
    const isSmallViewport = useBreakpointValue({
        base: true,
        md: false,
    });
    const isMobileOrTablet = useBreakpointValue({
        base: true,
        xl: false,
    });
    const { canOpenModal, onDismiss, onClick } = useCustomBanner({
        isSmallViewport,
        isMobileOrTablet,
        onDismissCallback,
    });
    const deviceProps = useMemo(() => !isSmallViewport
        ? {
            transform: canOpenModal
                ? "translate3d(0, 0, 0)"
                : "translate3d(calc(100% + 2rem), 0, 0)",
        }
        : {
            transform: canOpenModal
                ? "translate3d(0, 0, 0)"
                : "translate3d(0, calc(100% + 2rem), 0)",
        }, [isSmallViewport, canOpenModal]);
    const textRef = useRef(null);
    const [qrCodeSize, setQrCodeSize] = useState("110px");
    /**
     * Resize the QR code based on the height of the text container
     * to keep it square and aligned with the text height.
     *
     * This effect is only applied on larger viewports where the QR code is visible.
     */
    useLayoutEffect(() => {
        const textEl = textRef.current;
        const resizeQRCode = () => {
            const height = textEl?.offsetHeight;
            if (!height)
                return;
            setQrCodeSize(`${height}px`);
        };
        let resizeObserver = null;
        if (!isSmallViewport && textEl) {
            if ("ResizeObserver" in window) {
                resizeObserver = new ResizeObserver(resizeQRCode);
                resizeObserver.observe(textEl);
            }
            resizeQRCode();
        }
        return () => {
            resizeObserver?.disconnect();
        };
    }, [isSmallViewport, isMobileOrTablet]);
    if (canOpenModal === null)
        return null;
    const content = applicationId ? appBanner?.custom?.[applicationId] : null;
    if (!content)
        return null;
    const { translation, qrcode, store } = content;
    const deviceType = isSmallViewport ? "mobile" : "desktop";
    const title = t(`customAppBanner.${translation}.${deviceType}.title`);
    const description = t(`customAppBanner.${translation}.${deviceType}.description`);
    return (_jsxs(Box, { position: "fixed", display: "flex", flexDirection: {
            "breakpoint.base": "column",
            "breakpoint.md": "row",
        }, justifyContent: "space-between", zIndex: "zIndex.4", overflow: "hidden", gap: {
            "breakpoint.base": "spacing.24",
            "breakpoint.md": "spacing.16",
        }, bottom: {
            "breakpoint.base": "spacing.none",
            "breakpoint.md": "spacing.24",
        }, right: {
            "breakpoint.base": "spacing.none",
            "breakpoint.md": "spacing.24",
        }, width: "100%", maxWidth: {
            "breakpoint.md": "42.5rem",
        }, height: "fit-content", pointerEvents: "all", backgroundColor: {
            "breakpoint.base": "color.background.light",
            "breakpoint.md": "color.surface.brand.primary.default",
        }, borderColor: "color.content.interactive.inverted.default", borderTopStyle: "solid", borderTopWidth: "borderWidth.1", borderTopLeftRadius: "radius.16", borderTopRightRadius: "radius.16", borderBottomRightRadius: {
            "breakpoint.base": "radius.none",
            "breakpoint.md": "radius.16",
        }, borderBottomLeftRadius: {
            "breakpoint.base": "radius.none",
            "breakpoint.md": "radius.16",
        }, paddingTop: {
            "breakpoint.base": "spacing.40",
            "breakpoint.md": "spacing.16",
        }, paddingBottom: {
            "breakpoint.base": "spacing.40",
            "breakpoint.md": "spacing.16",
        }, paddingLeft: {
            "breakpoint.md": "spacing.16",
        }, paddingRight: {
            "breakpoint.md": "spacing.40",
        }, boxShadow: {
            "breakpoint.base": "shadow.32",
            "breakpoint.md": "shadow.16",
        }, color: "color.content.active.default", transitionDuration: "motionDuration.300", transitionTimingFunction: "motionEasing.ease-out", transitionProperty: "transform", ...deviceProps, children: [_jsx(Box, { position: "absolute", top: "0", right: "0", padding: "spacing.4", children: _jsx(Button, { size: "32", icon: CloseIcon, iconOnly: true, onPress: onDismiss, type: "button", variant: "tertiary", children: "Close" }) }), isSmallViewport ? (_jsx(Box, { as: "img", src: `${STATIC_ASSETS_PREFIX}/custom-banner/logo-brand.svg`, alt: "Brand Logo", alignSelf: "center" })) : (_jsx(Box, { as: "img", width: qrCodeSize, height: qrCodeSize, backgroundColor: "color.background.constant.white", borderRadius: "radius.8", padding: "spacing.4", src: `${STATIC_ASSETS_PREFIX}/custom-banner/${qrcode}`, alt: "QR Code Application" })), _jsxs(Box, { ref: textRef, display: "flex", flexDirection: "column", justifyContent: "space-between", width: "100%", paddingLeft: {
                    "breakpoint.base": "spacing.16",
                    "breakpoint.md": "spacing.none",
                }, paddingRight: {
                    "breakpoint.base": "spacing.16",
                    "breakpoint.md": "spacing.none",
                }, children: [_jsx(Box, { textAlign: {
                            "breakpoint.base": "center",
                            "breakpoint.md": "start",
                        }, paddingBottom: {
                            "breakpoint.base": "spacing.6",
                            "breakpoint.md": "spacing.8",
                        }, typography: "typography.headline.20.bold", children: title }), _jsx(Box, { textAlign: {
                            "breakpoint.base": "center",
                            "breakpoint.md": "start",
                        }, paddingBottom: {
                            "breakpoint.base": "spacing.24",
                            "breakpoint.md": "spacing.8",
                        }, typography: "typography.body.14.regular", children: description }), isSmallViewport ? (_jsx(Button, { onPress: () => {
                            onClick("CTA");
                        }, children: t(`customAppBanner.openTheApp`) })) : (_jsxs(Box, { display: "flex", gap: "spacing.16", justifyContent: {
                            "breakpoint.base": "center",
                            "breakpoint.md": "start",
                        }, children: [_jsx(StoreBadge, { type: "ios", store: store, isClickable: isMobileOrTablet, onClick: () => {
                                    onClick("App Store");
                                }, height: isSmallViewport ? "spacing.48" : "spacing.32" }), _jsx(StoreBadge, { type: "android", store: store, isClickable: isMobileOrTablet, onClick: () => {
                                    onClick("Google Play");
                                }, height: isSmallViewport ? "spacing.48" : "spacing.32" })] }))] })] }));
};
const StoreBadgeContent = {
    android: {
        imgSrc: `${STATIC_ASSETS_PREFIX}/custom-banner/store-android.svg`,
        alt: "Google Play Store",
    },
    ios: {
        imgSrc: `${STATIC_ASSETS_PREFIX}/custom-banner/store-ios.svg`,
        alt: "App Store",
    },
};
const StoreBadge = ({ type, store, isClickable, onClick, height, }) => {
    const href = store[type];
    const { imgSrc, alt } = StoreBadgeContent[type];
    const content = (_jsx(Box, { as: "img", src: imgSrc, alt: alt, height: height }));
    if (!isClickable) {
        return content;
    }
    return (_jsx(Box, { as: "a", href: href, target: "_blank", rel: "noopener noreferrer", onClick: onClick, children: content }));
};
